import React, { type PropsWithChildren, useCallback } from 'react';

import { type PrimaryProductKey, ProductKeys } from '../../../../common/constants/products';
import { outboundAuthConfigsSchema } from '../../../../common/constants/schemas/outbound-auth-config';
import { type SearchQueryStringParamsRaw } from '../../../../common/constants/schemas/query-params';
import { type UserDetails } from '../../../../common/constants/types';
import { useFetchConfig, type UseFetchConfigProps } from '../../../config';
import {
	BootstrapStoreContainer,
	type Components,
	type Features,
	type StyleOverrides,
	type TState,
} from '../index';

export type BootstrapContainerProps = PropsWithChildren<
	{
		/**
		 * The absolute url to the Atlassian Graphql Gateway. If not passed in the relative URL would be used.
		 */
		aggAbsoluteUrl?: string;

		/**
		 * Extra attributes to be attached to analytics events that are fired from within Full Page Search
		 */
		analyticsAttributes?: Record<string, any>;

		callbacks?: {
			/**
			 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
			 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
			 **/
			getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;

			/**
			 * A callback that is invoked when the query parameters in the URL need to be updated.
			 * The consuming product can implement the specific steps to be done when this callback is invoked.
			 *
			 * @param queryParamObject An object which is key:value map of things that should be set as the query parameter.
			 */
			updateQueryParameters?: (queryParamObject: Partial<SearchQueryStringParamsRaw>) => void;
		};

		/**
		 * This restricts the searches only to the cloudId that was passedin.
		 * This is a Jira/Confluence term and unfortunately most of the Atlassian products continue to be tied to a site.
		 *
		 * This propertly is useless for Trello or Bitbucket which are Workspace scoped.
		 *
		 * Not passing this property would bootstrap the UI in the site on which the user is most active
		 */
		cloudId?: string;

		components?: Components;

		/**
		 * Features that are enabled/disabled for the search page
		 * (!) Please note that the defaults for this prop is set in the initial state. Rely on useFeatures instead of props.
		 */
		features?: Features;

		isAdminHubAIEnabled?: boolean;

		orgId?: string;

		/**
		 * The product the search page will be rendered in.
		 */
		primaryProduct: PrimaryProductKey;

		/**
		 * Passing this will override the behaviour of bootstrapping in all product mode.
		 * The UI will only show the products which have been passed PROVIDED the user has access to them.
		 *
		 * NOTE: QS-4325: The `products` parameter is deprecated as we are removing it as a
		 * prop from the root <SearchPage/>.
		 */
		products?: ProductKeys[];

		queryParams?: Record<string, any>;
		SAINHostName?: string;

		/**
		 * A unique identifier for the search session. If not passed, a random UUID will be generated.
		 */
		searchSessionId?: string;

		styleOverrides?: StyleOverrides;

		/**
		 * Details of the current user.
		 */
		user?: UserDetails;
	} & Pick<
		UseFetchConfigProps,
		'configFetchAbsoluteUrl' | 'thirdPartyAbsoluteUrl' | 'aggregatorHostName'
	>
>;

export const BootstrapStoreProvider = ({
	children,
	...props
}: PropsWithChildren<BootstrapContainerProps>) => {
	const {
		cloudId,
		configFetchAbsoluteUrl,
		user,
		thirdPartyAbsoluteUrl,
		primaryProduct,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		orgId,
		analyticsAttributes,
		queryParams,
		features,
		searchSessionId,
		isAdminHubAIEnabled,
		callbacks,
		styleOverrides,
		components,
	} = props;

	const { accessibleProducts, outboundAuthConfigs, firstCloudId, intercomHmac, isNounsAvailable } =
		useFetchConfig({
			cloudId,
			configFetchAbsoluteUrl,
			aggregatorHostName,
			userId: user?.id,
			thirdPartyAbsoluteUrl,
			primaryProduct,
		});

	const getInitialProps = useCallback(() => {
		let storeContainerInitialProps: Partial<TState> = {
			cloudId: cloudId ?? firstCloudId,
			aggAbsoluteUrl,
			SAINHostName,
			aggregatorHostName,
			user,
			primaryProduct,
			orgId,
			analyticsAttributes,
			features,
			intercomHmac,
			isAdminHubAIEnabled,
			isNounsAvailable,
			products: [ProductKeys.Confluence],
			allFilterExcludedProducts: [ProductKeys.Trello],
			styleOverrides,
			components,
		};

		if (searchSessionId) {
			storeContainerInitialProps.searchSessionId = searchSessionId;
		}
		if (accessibleProducts) {
			const parsedOutboundAuth = outboundAuthConfigsSchema.safeParse(outboundAuthConfigs);
			if (parsedOutboundAuth.success) {
				storeContainerInitialProps.outboundAuthConfigs = outboundAuthConfigs;
			}
		}

		return storeContainerInitialProps;
	}, [
		cloudId,
		firstCloudId,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		user,
		primaryProduct,
		orgId,
		analyticsAttributes,
		features,
		intercomHmac,
		isAdminHubAIEnabled,
		isNounsAvailable,
		searchSessionId,
		accessibleProducts,
		outboundAuthConfigs,
		styleOverrides,
		components,
	]);

	return (
		<BootstrapStoreContainer
			initialProps={getInitialProps()}
			queryParams={queryParams}
			queryParamsCallback={callbacks?.updateQueryParameters}
			getSpaceIdsFromSpaceKeys={callbacks?.getSpaceIdsFromSpaceKeys}
		>
			{children}
		</BootstrapStoreContainer>
	);
};
